import PageToolbar from 'ui/PageToolbar/PageToolbar';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { Button, Input, Space, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
// Constants
import { EXTERNAL_TALENT_IMPORT } from 'constants/routes';
// Hooks
import { useDebouncedValue, useGetSports } from 'hooks';
// Api
import {
  ADMIN_EXPORT_EXT_TALENTS,
  ADMIN_EXT_TALENT_EXPORT_OPTIONS,
  ADMIN_SEARCH_EXT_TALENTS,
} from 'api/ExtTalent/queries';
// Types
import {
  AdminExportExtTalents,
  AdminExportExtTalentsVariables,
} from 'api/ExtTalent/types/AdminExportExtTalents';
import { AdminExtTalentExportOptions } from 'api/ExtTalent/types/AdminExtTalentExportOptions';
import {
  AdminSearchExtTalents,
  AdminSearchExtTalentsVariables,
  AdminSearchExtTalents_adminSearchExtTalents_entities,
} from 'api/ExtTalent/types/adminSearchExtTalents';
import { ExtTalentExportColumn } from 'api/graphql-global-types';
// Helpers
import {
  clearLocationFilters,
  ExternalTalentTableRow,
  getExternalTalentExportInput,
  getExternalTalentTableRows,
  getSearchVariables,
  LocationFilterPrefix,
  LocationFilterValues,
  updateLocationFilters,
  updateSingleFilter,
} from 'helpers/externalTalents';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
import Table, { TableFilter } from 'ui/Table';
// Components
import CreateEditExternalTalentModal from './components/CreateEditExternalTalentModal/CreateEditExternalTalentModal';
import ExportExternalTalentsModal from './components/ExportExternalTalentsModal/ExportExternalTalentsModal';
import { getExternalTalentColumns } from './columns';
// Styles
import styles from './ExternalTalents.module.scss';

const ExternalTalents = (): JSX.Element => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [queryInput, setQueryInput] = useState<string>('');
  const [appliedQuery, setAppliedQuery] = useState<string>('');
  const [selectedTalent, setSelectedTalent] =
    useState<AdminSearchExtTalents_adminSearchExtTalents_entities | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isExportModalVisible, setIsExportModalVisible] =
    useState<boolean>(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState<
    ExtTalentExportColumn[]
  >([]);
  const [isExportingExternalTalents, setIsExportingExternalTalents] =
    useState<boolean>(false);
  const apolloClient = useApolloClient();
  const history = useHistory();

  const { data: sportsData, loading: loadingSports } = useGetSports();
  const sportsList = sportsData?.sports;
  const sports = useMemo(() => sportsList || [], [sportsList]);
  const debouncedQuery = useDebouncedValue(queryInput.trim(), 1000);
  const appliedQueryRef = useRef<string>(appliedQuery);

  useEffect(() => {
    appliedQueryRef.current = appliedQuery;
  }, [appliedQuery]);

  useEffect(() => {
    if (debouncedQuery === appliedQueryRef.current) {
      return;
    }

    setCurrentPage(1);
    setAppliedQuery(debouncedQuery);
  }, [debouncedQuery]);

  const { data: exportOptionsData, loading: loadingExportOptions } =
    useQuery<AdminExtTalentExportOptions>(ADMIN_EXT_TALENT_EXPORT_OPTIONS, {
      fetchPolicy: 'cache-first',
    });

  const searchVariables = useMemo(
    () =>
      getSearchVariables({
        appliedQuery,
        currentPage,
        pageSize,
        searchFilters,
      }),
    [appliedQuery, currentPage, pageSize, searchFilters]
  );

  const { data, loading, refetch } = useQuery<
    AdminSearchExtTalents,
    AdminSearchExtTalentsVariables
  >(ADMIN_SEARCH_EXT_TALENTS, {
    fetchPolicy: 'cache-and-network',
    variables: searchVariables,
  });
  const talentEntities = data?.adminSearchExtTalents?.entities;
  const exportColumns =
    exportOptionsData?.adminExtTalentExportOptions?.columns || [];

  const tableData = useMemo(
    () => getExternalTalentTableRows(talentEntities, sports),
    [sports, talentEntities]
  );

  const handleQueryInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setQueryInput(event.target.value);
  };

  const handleCreateModalOpen = useCallback(() => {
    setSelectedTalent(null);
    setIsModalVisible(true);
  }, []);

  const handleExportModalOpen = useCallback(() => {
    setSelectedExportColumns([]);
    setIsExportModalVisible(true);
  }, []);

  const handleEditModalOpen = useCallback(
    (talent: AdminSearchExtTalents_adminSearchExtTalents_entities) => {
      setSelectedTalent(talent);
      setIsModalVisible(true);
    },
    []
  );

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
    setSelectedTalent(null);
  }, []);

  const handleExportModalClose = useCallback(() => {
    if (isExportingExternalTalents) {
      return;
    }

    setIsExportModalVisible(false);
  }, [isExportingExternalTalents]);

  const handleApplyLocationFilters = useCallback(
    (
      prefix: LocationFilterPrefix,
      values: LocationFilterValues,
      confirm: () => void
    ) => {
      setCurrentPage(1);
      setSearchFilters((currentFilters) =>
        updateLocationFilters(currentFilters, prefix, values)
      );
      confirm();
    },
    []
  );

  const handleResetLocationFilters = useCallback(
    (prefix: LocationFilterPrefix, clearFilters?: () => void) => {
      setCurrentPage(1);
      setSearchFilters((currentFilters) =>
        clearLocationFilters(currentFilters, prefix)
      );
      clearFilters?.();
    },
    []
  );

  const handleApplySportFilter = useCallback(
    (sport: string, confirm: () => void) => {
      setCurrentPage(1);
      setSearchFilters((currentFilters) =>
        updateSingleFilter(currentFilters, 'sports', sport)
      );
      confirm();
    },
    []
  );

  const handleResetSportFilter = useCallback((clearFilters?: () => void) => {
    setCurrentPage(1);
    setSearchFilters((currentFilters) =>
      updateSingleFilter(currentFilters, 'sports')
    );
    clearFilters?.();
  }, []);

  const handleModalSuccess = async () => {
    try {
      await refetch(searchVariables);
    } catch (error) {
      errorNotification(
        'External talent was saved, but refreshing the list failed.'
      );
    }
  };

  const handleExportColumnsChange = useCallback(
    (columns: ExtTalentExportColumn[]) => {
      setSelectedExportColumns(columns);
    },
    []
  );

  const handleExportExternalTalents = async () => {
    try {
      setIsExportingExternalTalents(true);

      const { data: exportData } = await apolloClient.query<
        AdminExportExtTalents,
        AdminExportExtTalentsVariables
      >({
        fetchPolicy: 'no-cache',
        query: ADMIN_EXPORT_EXT_TALENTS,
        variables: {
          input: getExternalTalentExportInput({
            appliedQuery,
            columns: selectedExportColumns,
            searchFilters,
          }),
        },
      });

      const csvUrl = exportData?.adminExportExtTalents?.csvUrl;

      if (!csvUrl) {
        errorNotification('CSV download could not be started.');
        return;
      }

      window.open(csvUrl, '_blank', 'noopener,noreferrer');
      successNotification('CSV has been downloaded');
      setIsExportModalVisible(false);
    } catch (error) {
      errorNotification('Error downloading CSV');
    } finally {
      setIsExportingExternalTalents(false);
    }
  };

  const columns = useMemo(
    () =>
      getExternalTalentColumns({
        loadingSports,
        onApplyLocationFilters: handleApplyLocationFilters,
        onApplySportFilter: handleApplySportFilter,
        onEditTalent: handleEditModalOpen,
        onResetLocationFilters: handleResetLocationFilters,
        onResetSportFilter: handleResetSportFilter,
        searchFilters,
        sports,
      }),
    [
      handleApplyLocationFilters,
      handleApplySportFilter,
      handleEditModalOpen,
      handleResetLocationFilters,
      handleResetSportFilter,
      loadingSports,
      searchFilters,
      sports,
    ]
  );

  return (
    <>
      <PageToolbar
        leading={
          <Space>
            <Input
              className={styles.queryInput}
              onChange={handleQueryInputChange}
              placeholder="Search external talents"
              value={queryInput}
            />

            <Tooltip title="Search talents by their first name, last name or profile name (socials).">
              <InfoCircleOutlined className={styles.infoIcon} />
            </Tooltip>
          </Space>
        }
      >
        <Button onClick={() => history.push(EXTERNAL_TALENT_IMPORT)}>
          Import CSV
        </Button>

        <Button onClick={handleExportModalOpen}>Export CSV</Button>

        <Button onClick={handleCreateModalOpen} type="primary">
          Create New
        </Button>
      </PageToolbar>

      <Table<ExternalTalentTableRow>
        columns={columns}
        currentPage={currentPage}
        data={tableData}
        loading={loading}
        pageSize={pageSize}
        scroll={{ x: 2300 }}
        searchFilters={searchFilters}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
        setSearchFilters={setSearchFilters}
        total={data?.adminSearchExtTalents?.total}
      />

      <CreateEditExternalTalentModal
        isVisible={isModalVisible}
        key={selectedTalent?.id || 'new'}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        talent={selectedTalent}
      />

      <ExportExternalTalentsModal
        exportColumns={exportColumns}
        isSubmitting={isExportingExternalTalents}
        isVisible={isExportModalVisible}
        loadingOptions={loadingExportOptions}
        onClose={handleExportModalClose}
        onColumnsChange={handleExportColumnsChange}
        onSubmit={handleExportExternalTalents}
        selectedColumns={selectedExportColumns}
      />
    </>
  );
};

export default ExternalTalents;
